import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  ChartLineUp, ChatCircleDots, UsersThree,
  Car, Gear, SignOut,
  MegaphoneSimple, Eye, GraduationCap, 
} from "@phosphor-icons/react";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import SdmLogo from "@/components/SdmLogo";

const ROLE_ITEMS = {
  admin: ["/", "/inbox", "/supervisor", "/leads", "/vehicles", "/campaigns", "/training", "/settings"],
  supervisor: ["/", "/inbox", "/supervisor", "/leads", "/vehicles", "/campaigns", "/training", "/settings"],
  // BDC agent — único rol operativo (post-remap Feb 2026). Segmentación por specialties.
  bdc_agent: ["/", "/inbox", "/leads", "/training"],
  // Legacy roles kept only so old JWT tokens don't 404 in the sidebar before their
  // owner triggers the auto-migration to bdc_agent at next login.
  bdc: ["/", "/inbox", "/leads", "/training"],
  sales: ["/", "/inbox", "/leads", "/training"],
  service: ["/", "/inbox", "/leads", "/training"],
  marketing: ["/", "/inbox", "/leads", "/training"],
};

const items = [
  { to: "/", key: "nav.dashboard", icon: ChartLineUp, testid: "nav-dashboard" },
  { to: "/inbox", key: "nav.inbox", icon: ChatCircleDots, testid: "nav-inbox" },
  { to: "/supervisor", key: "nav.supervisor", icon: Eye, testid: "nav-supervisor" },
  { to: "/leads", key: "nav.leads", icon: UsersThree, testid: "nav-leads" },
  { to: "/vehicles", key: "nav.vehicles", icon: Car, testid: "nav-vehicles" },
  { to: "/campaigns", key: "nav.campaigns", icon: MegaphoneSimple, testid: "nav-campaigns" },
  { to: "/training", key: "nav.training", icon: GraduationCap, testid: "nav-training" },
  { to: "/settings", key: "nav.settings", icon: Gear, testid: "nav-settings" },
];

const PRESENCE_DOT = {
  available: "bg-emerald-500",
  busy: "bg-amber-500",
  away: "bg-slate-400",
  offline: "bg-slate-500",
};

export default function Sidebar({ open = false }) {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <aside
      data-testid="app-sidebar"
      className={`w-64 flex-shrink-0 h-screen flex flex-col fixed inset-y-0 left-0 z-50 transition-transform duration-200 md:sticky md:top-0 md:translate-x-0 ${open ? "translate-x-0 shadow-2xl" : "-translate-x-full"}`}
      style={{ backgroundColor: "#0A1420", color: "#FFFFFF" }}
    >
      <div
        className="px-5 py-6 cursor-pointer flex items-center gap-3"
        onClick={() => nav("/")}
        data-testid="sidebar-brand"
      >
        <SdmLogo size={44} />
        <div>
          <div className="font-heading font-bold text-[10px] uppercase tracking-[0.18em] leading-none text-white/50">SDM Customer</div>
          <div className="font-heading font-extrabold text-sm tracking-[-0.03em] leading-none mt-1.5 text-white">Engagement Console</div>
        </div>
      </div>

      <nav className="flex-1 overflow-auto px-3 py-2 space-y-0.5">
        {items
          .filter((it) => {
            const allowed = ROLE_ITEMS[user?.role] || ROLE_ITEMS.admin;
            return allowed.includes(it.to);
          })
          .map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            data-testid={it.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                isActive
                  ? "bg-white text-[#0A1420] font-semibold"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <it.icon size={18} weight={isActive ? "fill" : "regular"} />
                <span>{t(it.key)}</span>
              </>
            )}
          </NavLink>
        ))}

      </nav>

      <div className="p-3">
        {user && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
            <div className="relative shrink-0">
              <div className="w-10 h-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-sm">
                {user.name?.[0]?.toUpperCase() || "U"}
              </div>
              <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0A1420] ${PRESENCE_DOT[user.presence] || PRESENCE_DOT.offline}`}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-white" data-testid="sidebar-user-name">{user.name}</div>
              <div className="text-[10px] text-white/50 uppercase tracking-[0.15em]">{user.role}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" className="rounded-full h-8 w-8 text-white/70 hover:text-white hover:bg-white/10">
              <SignOut size={15} />
            </Button>
          </div>
        )}
      </div>
    </aside>
  );
}
